import React, { useState, useEffect, useCallback } from 'react';
import './AdminDashboard.css';
import { formatCurrency, formatInteger } from '../utils/currency';
import { formatDate } from '../utils/dates';
import HarnessPanel from './HarnessPanel';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [adminKey, setAdminKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('clients');
  const [loading, setLoading] = useState(false);
  // Diagnostics tab state
  const [mrRefInput, setMrRefInput] = useState('');
  const [mrLookup, setMrLookup] = useState(null);
  const [mrLookupErr, setMrLookupErr] = useState('');
  const [mrLookupLoading, setMrLookupLoading] = useState(false);
  const [indexData, setIndexData] = useState(null);
  const [indexErr, setIndexErr] = useState('');
  const [indexLoading, setIndexLoading] = useState(false);
  const [recentData, setRecentData] = useState(null);
  const [recentErr, setRecentErr] = useState('');
  const [recentLoading, setRecentLoading] = useState(false);
  // Bulk-close stale MRs (danger zone) state
  const [bulkHours, setBulkHours] = useState(24);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkPreviewErr, setBulkPreviewErr] = useState('');
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkExecuting, setBulkExecuting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkResultErr, setBulkResultErr] = useState('');
  // Iter 22 Step 2D-a.1 — AI Scoping Gate state
  const [gateSettings, setGateSettings] = useState(null);
  const [gateSettingsErr, setGateSettingsErr] = useState('');
  const [gateSaving, setGateSaving] = useState(false);
  const [gateStats, setGateStats] = useState(null);
  const [gateLog, setGateLog] = useState([]);
  const [gateLogFilter, setGateLogFilter] = useState('all');
  const [gateLogLoading, setGateLogLoading] = useState(false);
  const [gateExpandedIdx, setGateExpandedIdx] = useState(null);
  // Item 1 (2026-02) — Classifier Health Panel state.
  // System-admin diagnostics only. Reads from
  // `/api/admin/classifier-health` which aggregates
  // `maintenance_action_log` rows (site=classifier_health +
  // site=bug4_answerer) over the last 7 days.
  const [chHealth, setChHealth] = useState(null);
  const [chHealthErr, setChHealthErr] = useState('');
  const [chHealthLoading, setChHealthLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const [sRes, cRes, aRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/summary`, { headers: { 'X-Admin-Key': adminKey } }),
        fetch(`${API_BASE_URL}/api/admin/clients`, { headers: { 'X-Admin-Key': adminKey } }),
        fetch(`${API_BASE_URL}/api/admin/alerts`, { headers: { 'X-Admin-Key': adminKey } }),
      ]);
      if (sRes.status === 401) { setAuthError(true); setAuthenticated(false); return; }
      setAuthError(false);
      setAuthenticated(true);
      const sText = await sRes.text();
      const cText = await cRes.text();
      const aText = await aRes.text();
      setSummary(sText ? JSON.parse(sText) : null);
      setClients(cText ? JSON.parse(cText) : []);
      setAlerts(aText ? JSON.parse(aText) : []);
    } catch { setAuthError(true); }
    finally { setLoading(false); }
  }, [adminKey]);

  const handleLogin = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleUpdateThreshold = async (clientId, value) => {
    try {
      await fetch(`${API_BASE_URL}/api/admin/clients/${clientId}/threshold`, {
        method: 'PUT',
        headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ usage_alert_threshold: value || null }),
      });
      fetchData();
    } catch {}
  };

  const lookupMrRef = async (e) => {
    if (e) e.preventDefault();
    const ref = mrRefInput.trim();
    setMrLookup(null);
    setMrLookupErr('');
    if (!ref) { setMrLookupErr('Enter a reference'); return; }
    setMrLookupLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/maintenance/by-ref/${encodeURIComponent(ref)}`,
        { headers: { 'X-Admin-Key': adminKey } }
      );
      if (!res.ok) {
        setMrLookupErr(`Lookup failed: HTTP ${res.status}`);
      } else {
        setMrLookup(await res.json());
      }
    } catch (err) {
      setMrLookupErr(String(err));
    } finally {
      setMrLookupLoading(false);
    }
  };

  const fetchIndexes = async () => {
    setIndexData(null);
    setIndexErr('');
    setIndexLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/db/indexes/maintenance_requests`,
        { headers: { 'X-Admin-Key': adminKey } }
      );
      if (!res.ok) {
        setIndexErr(`Fetch failed: HTTP ${res.status}`);
      } else {
        setIndexData(await res.json());
      }
    } catch (err) {
      setIndexErr(String(err));
    } finally {
      setIndexLoading(false);
    }
  };

  const fetchRecent = async () => {
    setRecentData(null);
    setRecentErr('');
    setRecentLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/maintenance/recent?limit=25`,
        { headers: { 'X-Admin-Key': adminKey } }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        setRecentErr(`Fetch failed: HTTP ${res.status} ${txt.slice(0, 200)}`);
      } else {
        setRecentData(await res.json());
      }
    } catch (err) {
      setRecentErr(String(err));
    } finally {
      setRecentLoading(false);
    }
  };

  const runBulkClosePreview = async () => {
    setBulkPreview(null);
    setBulkPreviewErr('');
    setBulkResult(null);
    setBulkResultErr('');
    const h = parseInt(bulkHours, 10);
    if (isNaN(h) || h < 1) { setBulkPreviewErr('Hours must be a positive integer'); return; }
    setBulkPreviewLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/maintenance/bulk-close-stale`,
        {
          method: 'POST',
          headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
          body: JSON.stringify({ hours: h, dry_run: true }),
        }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        setBulkPreviewErr(`Preview failed: HTTP ${res.status} ${txt.slice(0, 200)}`);
      } else {
        setBulkPreview(await res.json());
      }
    } catch (err) {
      setBulkPreviewErr(String(err));
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const runBulkCloseExecute = async () => {
    if (!bulkPreview) return;
    setBulkResultErr('');
    setBulkExecuting(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/maintenance/bulk-close-stale`,
        {
          method: 'POST',
          headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
          body: JSON.stringify({ hours: bulkPreview.hours, dry_run: false }),
        }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        setBulkResultErr(`Execute failed: HTTP ${res.status} ${txt.slice(0, 200)}`);
      } else {
        setBulkResult(await res.json());
        setBulkPreview(null);
        // Refresh recent MRs so archived rows drop from the list
        fetchRecent();
      }
    } catch (err) {
      setBulkResultErr(String(err));
    } finally {
      setBulkExecuting(false);
    }
  };

  const cancelBulkClose = () => {
    setBulkPreview(null);
    setBulkPreviewErr('');
  };

  // Iter 22 Step 2D-a.1 — AI Scoping Gate handlers
  const fetchGateSettings = async () => {
    setGateSettingsErr('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/scoping-gate/settings`,
        { headers: { 'X-Admin-Key': adminKey } },
      );
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) { setGateSettingsErr(data?.detail || 'Failed'); return; }
      setGateSettings(data);
    } catch (e) { setGateSettingsErr(String(e.message || e)); }
  };
  const fetchGateStats = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/scoping-gate/stats?since_hours=168`,
        { headers: { 'X-Admin-Key': adminKey } },
      );
      const text = await res.text();
      setGateStats(text ? JSON.parse(text) : null);
    } catch { /* silent — stats card just won't render */ }
  };
  const fetchGateLog = async (filter = gateLogFilter) => {
    setGateLogLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/scoping-gate/log?limit=100&mode=${filter}`,
        { headers: { 'X-Admin-Key': adminKey } },
      );
      const text = await res.text();
      const data = text ? JSON.parse(text) : { rows: [] };
      setGateLog(data.rows || []);
    } catch { setGateLog([]); }
    finally { setGateLogLoading(false); }
  };
  const updateGateSettings = async (patch) => {
    setGateSaving(true);
    setGateSettingsErr('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/scoping-gate/settings`,
        {
          method: 'POST',
          headers: {
            'X-Admin-Key': adminKey,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(patch),
        },
      );
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) {
        setGateSettingsErr(data?.detail || 'Save failed');
      } else {
        setGateSettings(data);
      }
    } catch (e) { setGateSettingsErr(String(e.message || e)); }
    finally { setGateSaving(false); }
  };

  // Item 1 (2026-02) — Classifier Health Panel fetcher. Read-
  // only aggregation; timeout rate is the key signal — the recent
  // timeout bug silently broke Bug 4, so we want early warning
  // if latency creeps back toward the 12s ceiling.
  const fetchClassifierHealth = async () => {
    setChHealthLoading(true);
    setChHealthErr('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/classifier-health?since_hours=168`,
        { headers: { 'X-Admin-Key': adminKey } },
      );
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) {
        setChHealthErr(data?.detail || 'Failed to fetch health');
      } else {
        setChHealth(data);
      }
    } catch (e) { setChHealthErr(String(e.message || e)); }
    finally { setChHealthLoading(false); }
  };

  useEffect(() => {
    if (authenticated && activeTab === 'diagnostics') {
      if (!indexData && !indexLoading) fetchIndexes();
      if (!recentData && !recentLoading) fetchRecent();
      if (!gateSettings) { fetchGateSettings(); fetchGateStats(); fetchGateLog('all'); }
      if (!chHealth && !chHealthLoading) fetchClassifierHealth();
    }
  }, [authenticated, activeTab]);

  if (!authenticated) {
    return (
      <div className="admin-login-wrap" data-testid="admin-login">
        <form className="admin-login-card" onSubmit={handleLogin}>
          <h2>Estatel AI Admin</h2>
          <p>Enter your admin key to access the owner dashboard.</p>
          {authError && <div className="admin-error" data-testid="admin-auth-error">Invalid admin key</div>}
          <input
            type="password"
            value={adminKey}
            onChange={e => setAdminKey(e.target.value)}
            placeholder="Admin Key"
            autoFocus
            data-testid="admin-key-input"
          />
          <button type="submit" className="admin-btn-primary" disabled={!adminKey} data-testid="admin-login-btn">
            Access Dashboard
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-wrap" data-testid="admin-dashboard">
      <header className="admin-header">
        <h1>Estatel AI Owner Dashboard</h1>
        <span className="admin-badge">ADMIN</span>
      </header>

      {/* Platform Summary */}
      {summary && (
        <div className="admin-summary" data-testid="admin-summary">
          <div className="as-card">
            <span className="as-value">{summary.total_active_clients}</span>
            <span className="as-label">Active Clients</span>
          </div>
          <div className="as-card">
            <span className="as-value">{formatCurrency(summary.total_mrr)}</span>
            <span className="as-label">MRR</span>
          </div>
          <div className="as-card">
            <span className="as-value">{formatCurrency(summary.total_api_cost)}</span>
            <span className="as-label">API Cost (Month)</span>
          </div>
          <div className="as-card">
            <span className="as-value as-margin">{formatCurrency(summary.gross_margin)}</span>
            <span className="as-label">Gross Margin</span>
          </div>
          <div className="as-card">
            <span className="as-value">{formatCurrency(summary.avg_cost_per_client, { decimals: 4 })}</span>
            <span className="as-label">Avg Cost / Client</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button className={`admin-tab ${activeTab === 'clients' ? 'active' : ''}`} onClick={() => setActiveTab('clients')} data-testid="admin-tab-clients">
          Clients ({clients.length})
        </button>
        <button className={`admin-tab ${activeTab === 'alerts' ? 'active' : ''}`} onClick={() => setActiveTab('alerts')} data-testid="admin-tab-alerts">
          Alerts ({alerts.length})
        </button>
        <button className={`admin-tab ${activeTab === 'diagnostics' ? 'active' : ''}`} onClick={() => setActiveTab('diagnostics')} data-testid="admin-tab-diagnostics">
          Diagnostics
        </button>
        <button className={`admin-tab ${activeTab === 'harness' ? 'active' : ''}`} onClick={() => setActiveTab('harness')} data-testid="admin-tab-harness">
          Harness
        </button>
      </div>

      {loading && <p className="admin-loading">Loading...</p>}

      {/* Clients Table */}
      {activeTab === 'clients' && (
        <div className="admin-table-wrap" data-testid="admin-clients-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Tier</th>
                <th>Status</th>
                <th>MRR</th>
                <th>Tokens</th>
                <th>API Cost</th>
                <th>Margin</th>
                <th>Alert Threshold</th>
                <th>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id} className={`admin-row admin-row-${c.colour}`} data-testid={`admin-client-row-${c.id}`}>
                  <td className="admin-cell-name">{c.company_name}</td>
                  <td><span className="admin-tier-badge">{c.tier || 'none'}</span></td>
                  <td><span className={`admin-status-badge admin-status-${c.status}`}>{c.status}</span></td>
                  <td>{formatCurrency(c.mrr)}</td>
                  <td>{formatInteger(c.tokens_this_month)}</td>
                  <td className="admin-cell-cost">{formatCurrency(c.api_cost, { decimals: 4 })}</td>
                  <td className={c.margin < 0 ? 'admin-cell-negative' : ''}>{formatCurrency(c.margin)}</td>
                  <td>
                    <ThresholdInput clientId={c.id} currentValue={c.alert_threshold} onSave={handleUpdateThreshold} />
                  </td>
                  <td className="admin-cell-date">{formatDate(c.last_active, '-')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Alerts Table */}
      {activeTab === 'alerts' && (
        <div className="admin-table-wrap" data-testid="admin-alerts-table">
          {alerts.length === 0 ? (
            <p className="admin-no-alerts">No usage alerts triggered this billing cycle.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Alert Threshold</th>
                  <th>Actual API Cost</th>
                  <th>Overage</th>
                  <th>Update Threshold</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map(a => (
                  <tr key={a.id} data-testid={`admin-alert-row-${a.id}`}>
                    <td className="admin-cell-name">{a.company_name}</td>
                    <td>{formatCurrency(a.alert_threshold)}</td>
                    <td className="admin-cell-cost">{formatCurrency(a.api_cost, { decimals: 4 })}</td>
                    <td className="admin-cell-negative">{formatCurrency(a.overage, { decimals: 4 })}</td>
                    <td>
                      <ThresholdInput clientId={a.id} currentValue={a.alert_threshold} onSave={handleUpdateThreshold} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Diagnostics Tab — Maintenance dedup index + MR lookup */}
      {activeTab === 'diagnostics' && (
        <div className="admin-diagnostics" data-testid="admin-diagnostics">
          {/* Maintenance Tools — DANGER ZONE */}
          <section
            className="diag-card diag-danger-zone"
            data-testid="diag-bulk-close-card"
          >
            <div className="diag-card-head">
              <h3>
                <span className="diag-danger-badge">DANGER ZONE</span>
                Maintenance Tools — Bulk Close Stale MRs
              </h3>
            </div>
            <p className="diag-help">
              Closes v2 maintenance requests that are either in{' '}
              <code>coord_state=awaiting_tenant_availability</code> or were
              created more than <b>N hours</b> ago and are not already
              terminal / archived. Runs a <b>dry-run preview</b> first —
              nothing is modified until you confirm.
            </p>

            <div className="diag-bulk-controls">
              <label className="diag-bulk-label">
                Hours cutoff:
                <input
                  type="number"
                  min="1"
                  max="8760"
                  value={bulkHours}
                  onChange={e => setBulkHours(e.target.value)}
                  disabled={bulkPreviewLoading || bulkExecuting || !!bulkPreview}
                  className="diag-input diag-bulk-hours"
                  data-testid="bulk-close-hours-input"
                />
              </label>
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={runBulkClosePreview}
                disabled={bulkPreviewLoading || bulkExecuting || !!bulkPreview}
                data-testid="bulk-close-preview-btn"
              >
                {bulkPreviewLoading ? 'Previewing…' : 'Preview (dry-run)'}
              </button>
            </div>

            {bulkPreviewErr && (
              <div className="admin-error" data-testid="bulk-close-preview-error">
                {bulkPreviewErr}
              </div>
            )}

            {bulkPreview && (
              <div className="diag-bulk-preview" data-testid="bulk-close-preview">
                <div className="diag-bulk-preview-head">
                  Dry-run: <b>{bulkPreview.count}</b> maintenance request
                  {bulkPreview.count === 1 ? '' : 's'} would be closed
                  (cutoff <code>{bulkPreview.cutoff_utc}</code>).
                </div>

                {bulkPreview.count > 0 && (
                  <table className="admin-table diag-index-table" data-testid="bulk-close-preview-table">
                    <thead>
                      <tr>
                        <th>Ref</th>
                        <th>State</th>
                        <th>Status</th>
                        <th>Tenant</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPreview.sample.map(m => (
                        <tr key={m.ref} data-testid={`bulk-close-preview-row-${m.ref}`}>
                          <td className="diag-mono"><b>{m.ref || '—'}</b></td>
                          <td className="diag-mono">{m.coord_state || '—'}</td>
                          <td className="diag-mono">{m.status || '—'}</td>
                          <td className="diag-mono diag-truncate" title={m.tenant_email || ''}>
                            {m.tenant_email || '—'}
                          </td>
                          <td className="diag-mono diag-nowrap">
                            {m.created_at ? m.created_at.replace('T', ' ').slice(0, 19) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {bulkPreview.count > bulkPreview.sample.length && (
                  <p className="diag-help">
                    Showing first {bulkPreview.sample.length} of{' '}
                    {bulkPreview.count}. All matching refs will be closed on
                    execute.
                  </p>
                )}

                <div className="diag-bulk-actions">
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={cancelBulkClose}
                    disabled={bulkExecuting}
                    data-testid="bulk-close-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="admin-btn-danger"
                    onClick={runBulkCloseExecute}
                    disabled={bulkExecuting || bulkPreview.count === 0}
                    data-testid="bulk-close-execute-btn"
                  >
                    {bulkExecuting
                      ? 'Closing…'
                      : `Confirm & close ${bulkPreview.count} MR${bulkPreview.count === 1 ? '' : 's'}`}
                  </button>
                </div>
              </div>
            )}

            {bulkResultErr && (
              <div className="admin-error" data-testid="bulk-close-result-error">
                {bulkResultErr}
              </div>
            )}

            {bulkResult && (
              <div className="diag-bulk-result" data-testid="bulk-close-result">
                ✓ Closed <b>{bulkResult.count}</b> maintenance request
                {bulkResult.count === 1 ? '' : 's'} (cutoff{' '}
                <code>{bulkResult.cutoff_utc}</code>).
              </div>
            )}
          </section>

          {/* Index status card */}
          <section className="diag-card" data-testid="diag-index-card">
            <div className="diag-card-head">
              <h3>Maintenance Requests — Live Indexes</h3>
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={fetchIndexes}
                disabled={indexLoading}
                data-testid="diag-refresh-indexes-btn"
              >
                {indexLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {indexErr && <div className="admin-error" data-testid="diag-index-error">{indexErr}</div>}
            {indexData && (
              <>
                <div
                  className={`diag-index-status ${indexData.target_index_present ? 'ok' : 'bad'}`}
                  data-testid="diag-target-index-status"
                >
                  {indexData.target_index_present
                    ? `✓ uniq_agent_msgid_partial is ACTIVE on this database`
                    : `✗ uniq_agent_msgid_partial is NOT present on this database`}
                </div>
                <table className="admin-table diag-index-table" data-testid="diag-index-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th>Unique</th>
                      <th>Partial Filter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indexData.indexes.map(ix => (
                      <tr
                        key={ix.name}
                        className={ix.name === indexData.target_index_name ? 'diag-row-highlight' : ''}
                        data-testid={`diag-index-row-${ix.name}`}
                      >
                        <td className="diag-mono">{ix.name}</td>
                        <td className="diag-mono">{ix.key.map(([k, v]) => `${k}:${v}`).join(', ')}</td>
                        <td>{ix.unique ? 'YES' : 'no'}</td>
                        <td className="diag-mono">
                          {ix.partialFilterExpression ? JSON.stringify(ix.partialFilterExpression) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>

          {/* MR lookup card */}
          <section className="diag-card" data-testid="diag-mr-card">
            <div className="diag-card-head">
              <h3>Maintenance Request Lookup</h3>
            </div>
            <p className="diag-help">
              Enter a contractor reference (e.g. <code>MR-V2-AC7A56</code>) to see the stored dedup fields.
            </p>
            <form className="diag-lookup-form" onSubmit={lookupMrRef}>
              <input
                type="text"
                className="diag-input"
                value={mrRefInput}
                onChange={e => setMrRefInput(e.target.value)}
                placeholder="MR-V2-XXXXXX"
                data-testid="diag-mr-ref-input"
              />
              <button
                type="submit"
                className="admin-btn-primary"
                disabled={mrLookupLoading || !mrRefInput.trim()}
                data-testid="diag-mr-lookup-btn"
              >
                {mrLookupLoading ? 'Looking up…' : 'Look up'}
              </button>
            </form>
            {mrLookupErr && <div className="admin-error" data-testid="diag-mr-lookup-error">{mrLookupErr}</div>}
            {mrLookup && (
              <div className="diag-mr-result" data-testid="diag-mr-result">
                <div className="diag-mr-summary">
                  Ref <code>{mrLookup.ref}</code> — <b>{mrLookup.match_count}</b> match{mrLookup.match_count === 1 ? '' : 'es'}
                </div>
                {mrLookup.matches.length === 0 && (
                  <div className="diag-mr-none">No maintenance request found with that reference.</div>
                )}
                {mrLookup.matches.map(m => (
                  <div key={m.id} className="diag-mr-row" data-testid={`diag-mr-row-${m.id}`}>
                    <div className="diag-mr-line"><span>ID</span><code>{m.id}</code></div>
                    <div className="diag-mr-line"><span>contractor_ref</span><code>{m.contractor_ref}</code></div>
                    <div className="diag-mr-line"><span>agent_client_id</span><code>{m.agent_client_id || '—'}</code></div>
                    <div className="diag-mr-line"><span>coord_version</span><code>{m.coord_version || '—'}</code></div>
                    <div className="diag-mr-line diag-mr-critical">
                      <span>inbound_message_id</span>
                      <code>{m.inbound_message_id === null || m.inbound_message_id === undefined ? 'NULL' : m.inbound_message_id}</code>
                    </div>
                    <div className="diag-mr-line"><span>inbound_subject_hash</span><code>{m.inbound_subject_hash || '—'}</code></div>
                    <div className="diag-mr-line"><span>inbound_body_hash</span><code>{m.inbound_body_hash || '—'}</code></div>
                    <div className="diag-mr-line"><span>source</span><code>{m.source || '—'}</code></div>
                    <div className="diag-mr-line"><span>tenant_id</span><code>{m.tenant_id || '—'}</code></div>
                    <div className="diag-mr-line"><span>created_at</span><code>{m.created_at || '—'}</code></div>
                    <div className="diag-mr-line"><span>status</span><code>{m.status || '—'}</code></div>
                    <div className="diag-mr-line"><span>archived</span><code>{m.archived ? 'true' : 'false'}</code></div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Recent MRs card */}
          {/* Iter 22 Step 2D-a.1 — AI Scoping Gate (mode toggle + log) */}
          <section
            className="diag-card"
            data-testid="diag-scoping-gate-card"
          >
            <div className="diag-card-head">
              <h3>AI Scoping Gate</h3>
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={() => {
                  fetchGateSettings();
                  fetchGateStats();
                  fetchGateLog(gateLogFilter);
                }}
                data-testid="gate-refresh-btn"
              >
                Refresh
              </button>
            </div>
            <p className="diag-help">
              Decides whether an inbound tenant email is genuine
              maintenance (create MR), a general enquiry (route
              away), or ambiguous (surface for human review).{' '}
              Use <b>Shadow</b> to observe verdicts without acting.{' '}
              Flip to <b>Active</b> when you&apos;re satisfied.
            </p>

            {gateSettingsErr && (
              <div
                className="admin-error"
                data-testid="gate-settings-error"
              >
                {gateSettingsErr}
              </div>
            )}

            {gateSettings && (
              <div
                className="diag-bulk-controls"
                style={{ flexWrap: 'wrap', gap: '12px' }}
                data-testid="gate-controls"
              >
                <div>
                  <div style={{
                    fontSize: '12px', color: '#666',
                    marginBottom: 4,
                  }}>
                    Current mode:{' '}
                    <b
                      style={{
                        color: gateSettings.mode === 'active'
                          ? '#10b981'
                          : gateSettings.mode === 'shadow'
                            ? '#f59e0b'
                            : '#6b7280',
                      }}
                      data-testid="gate-current-mode"
                    >
                      {gateSettings.mode.toUpperCase()}
                    </b>
                    {' · '}source: {gateSettings.source}
                  </div>
                  <div
                    className="diag-bulk-controls"
                    style={{ gap: '6px' }}
                  >
                    {['off', 'shadow', 'active'].map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={
                          gateSettings.mode === m
                            ? 'admin-btn-primary'
                            : 'admin-btn-secondary'
                        }
                        disabled={
                          gateSaving || gateSettings.mode === m
                        }
                        onClick={() => updateGateSettings({
                          mode: m,
                          extraction_enabled: m !== 'off',
                        })}
                        data-testid={`gate-mode-btn-${m}`}
                      >
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <label
                  className="diag-bulk-label"
                  style={{ marginLeft: 8 }}
                >
                  Confidence threshold:
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={gateSettings.conf_high}
                    onChange={(e) => setGateSettings({
                      ...gateSettings,
                      conf_high: parseFloat(e.target.value),
                    })}
                    onBlur={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v)) {
                        updateGateSettings({ conf_high: v });
                      }
                    }}
                    disabled={gateSaving}
                    className="diag-input diag-bulk-hours"
                    data-testid="gate-conf-high-input"
                  />
                </label>
              </div>
            )}

            {gateStats && (
              <div
                style={{
                  marginTop: 12, padding: 12,
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb', borderRadius: 6,
                }}
                data-testid="gate-stats"
              >
                <div style={{ fontSize: 13, marginBottom: 6 }}>
                  <b>Last 7 days:</b> {gateStats.total} decision
                  {gateStats.total === 1 ? '' : 's'}{' · '}
                  <span style={{ color: '#059669' }}>
                    {gateStats.counts.create_mr} would-have create MR
                  </span>{' · '}
                  <span style={{ color: '#6b7280' }}>
                    {gateStats.counts.route_enquiry} enquiry
                  </span>{' · '}
                  <span style={{ color: '#d97706' }}>
                    {gateStats.counts.surface_for_review} surface for review
                  </span>
                  {gateStats.avg_llm_confidence !== null && (
                    <>
                      {' · '}avg LLM conf{' '}
                      <b>{gateStats.avg_llm_confidence}</b>
                    </>
                  )}
                </div>
                {gateStats.shadow_total > 0 && (
                  <div style={{ fontSize: 12, color: '#374151' }}>
                    In <b>shadow</b>: {gateStats.shadow_divergent} of{' '}
                    {gateStats.shadow_total} would have differed from
                    legacy behaviour{' '}
                    ({gateStats.shadow_total === 0
                      ? '0'
                      : Math.round(
                          gateStats.shadow_divergent
                            / gateStats.shadow_total * 100
                        )}
                    %). Review before flipping to active.
                  </div>
                )}
              </div>
            )}

            <div
              style={{ marginTop: 12, display: 'flex', gap: 8 }}
              data-testid="gate-log-filter-controls"
            >
              <span style={{ fontSize: 12, color: '#666' }}>
                Log filter:
              </span>
              {['all', 'shadow', 'active'].map((f) => (
                <button
                  key={f}
                  type="button"
                  className={
                    gateLogFilter === f
                      ? 'admin-btn-primary'
                      : 'admin-btn-secondary'
                  }
                  onClick={() => {
                    setGateLogFilter(f);
                    fetchGateLog(f);
                  }}
                  data-testid={`gate-log-filter-${f}`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              {gateLogLoading && (
                <span style={{ fontSize: 12 }}>Loading…</span>
              )}
            </div>

            <div
              className="diag-recent-table-wrap"
              style={{ marginTop: 8 }}
              data-testid="gate-log-table"
            >
              <table className="admin-table diag-recent-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Tenant</th>
                    <th>Subject</th>
                    <th>Mode</th>
                    <th>Would-have</th>
                    <th>AI intent</th>
                    <th>Conf</th>
                  </tr>
                </thead>
                <tbody>
                  {gateLog.length === 0 && (
                    <tr>
                      <td
                        colSpan="7"
                        style={{
                          textAlign: 'center',
                          padding: 16, color: '#6b7280',
                        }}
                      >
                        No scoping-gate decisions logged yet.
                        Turn on Shadow mode above and drive some
                        traffic.
                      </td>
                    </tr>
                  )}
                  {gateLog.map((r, i) => {
                    const wouldColor = r.would_have === 'create_mr'
                      ? '#059669'
                      : r.would_have === 'surface_for_review'
                        ? '#d97706'
                        : '#6b7280';
                    const isOpen = gateExpandedIdx === i;
                    return (
                      <React.Fragment key={i}>
                        <tr
                          onClick={() => setGateExpandedIdx(
                            isOpen ? null : i,
                          )}
                          style={{ cursor: 'pointer' }}
                          data-testid={`gate-log-row-${i}`}
                        >
                          <td className="diag-mono diag-nowrap">
                            {r.ts ? r.ts.replace('T', ' ').slice(0, 19)
                                   : '—'}
                          </td>
                          <td
                            className="diag-mono diag-truncate"
                            title={r.tenant_email || ''}
                          >
                            {r.tenant_email || '—'}
                          </td>
                          <td
                            className="diag-truncate"
                            title={r.subject || ''}
                            style={{ maxWidth: 240 }}
                          >
                            {r.subject || '—'}
                          </td>
                          <td className="diag-mono">
                            <b>{r.mode || '—'}</b>
                          </td>
                          <td
                            className="diag-mono"
                            style={{ color: wouldColor }}
                          >
                            <b>{r.would_have || '—'}</b>
                          </td>
                          <td className="diag-mono">
                            {r.llm_called
                              ? (r.llm_intent || '—')
                              : <i style={{ color: '#9ca3af' }}>
                                  tier2 hit
                                </i>}
                          </td>
                          <td className="diag-mono">
                            {r.llm_confidence != null
                              ? r.llm_confidence.toFixed(2)
                              : '—'}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr data-testid={`gate-log-row-detail-${i}`}>
                            <td
                              colSpan="7"
                              style={{
                                background: '#f9fafb',
                                padding: 12,
                                fontSize: 12,
                              }}
                            >
                              <div>
                                <b>Reason:</b> {r.reason || '—'}
                              </div>
                              <div>
                                <b>Tier 2 pass:</b>{' '}
                                {String(r.tier2_pass)}
                                {' · '}
                                <b>Actually did:</b>{' '}
                                {r.actually_did}
                                {' · '}
                                <b>Threshold at time:</b>{' '}
                                {r.conf_high != null
                                  ? r.conf_high
                                  : '—'}
                              </div>
                              <div style={{ marginTop: 6 }}>
                                <b>Body preview:</b>
                                <pre style={{
                                  whiteSpace: 'pre-wrap',
                                  marginTop: 4, fontSize: 12,
                                }}>{r.body_preview || '—'}</pre>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Item 1 (2026-02) — Classifier Health Panel.
              Small, quiet card next to the scoping-gate card.
              Not agent-facing. Reads existing
              `maintenance_action_log` rows over the last 7d. The
              key signal is `timeout_calls` — the recent bug that
              silently broke Bug 4 was a latency creep to the 12s
              ceiling; this panel flags that early. */}
          <section
            className="diag-card"
            data-testid="diag-classifier-health-card"
          >
            <div className="diag-card-head">
              <h3>Classifier Health (7d)</h3>
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={fetchClassifierHealth}
                disabled={chHealthLoading}
                data-testid="classifier-health-refresh-btn"
              >
                {chHealthLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            <p className="diag-help">
              Follow-up classifier + Bug 4 answerer over the last 7 days.
              Timeout rate is the key signal — if it starts creeping,
              Claude latency is pushing the 12s ceiling and Bug 4 will
              silently regress.
            </p>
            {chHealthErr && (
              <div className="admin-error" data-testid="classifier-health-error">
                {chHealthErr}
              </div>
            )}
            {chHealth && (
              <>
                <div
                  className="diag-stats-grid"
                  data-testid="classifier-health-stats"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 12, marginTop: 12,
                  }}
                >
                  <div className="diag-stat-card">
                    <div className="diag-stat-label">Total calls</div>
                    <div
                      className="diag-stat-value"
                      data-testid="ch-total-calls"
                    >
                      {chHealth.total_classifier_calls}
                    </div>
                  </div>
                  <div className="diag-stat-card">
                    <div className="diag-stat-label">Timeouts</div>
                    <div
                      className="diag-stat-value"
                      data-testid="ch-timeout-calls"
                      style={{
                        color: (chHealth.timeout_calls || 0) > 0
                          ? '#d97706' : '#059669',
                      }}
                    >
                      {chHealth.timeout_calls}
                    </div>
                  </div>
                  <div className="diag-stat-card">
                    <div className="diag-stat-label">Errors</div>
                    <div
                      className="diag-stat-value"
                      data-testid="ch-error-calls"
                    >
                      {chHealth.error_calls}
                    </div>
                  </div>
                  <div className="diag-stat-card">
                    <div className="diag-stat-label">Avg retries</div>
                    <div
                      className="diag-stat-value"
                      data-testid="ch-avg-retry"
                      title={
                        '1.00 = never retried. 2.00 = every call needed a retry.'
                      }
                    >
                      {chHealth.avg_retry_count != null
                        ? chHealth.avg_retry_count.toFixed(2)
                        : '—'}
                    </div>
                  </div>
                </div>

                {/* Bug 4 outcome mix */}
                <div style={{ marginTop: 16 }}>
                  <h4 style={{
                    fontSize: 13, fontWeight: 600,
                    color: '#374151', marginBottom: 8,
                  }}>
                    Bug 4 outcomes (last 7d) — total {chHealth.bug4_total}
                  </h4>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: 8,
                    }}
                    data-testid="ch-bug4-outcomes"
                  >
                    <div className="diag-stat-card" style={{ padding: 8 }}>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>Auto-answered</div>
                      <div
                        style={{ fontSize: 20, fontWeight: 600, color: '#059669' }}
                        data-testid="ch-bug4-answered"
                      >
                        {chHealth.bug4_outcomes?.auto_answered ?? 0}
                      </div>
                    </div>
                    <div className="diag-stat-card" style={{ padding: 8 }}>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>Held for review</div>
                      <div
                        style={{ fontSize: 20, fontWeight: 600, color: '#0284c7' }}
                        data-testid="ch-bug4-held"
                      >
                        {chHealth.bug4_outcomes?.held_for_review ?? 0}
                      </div>
                    </div>
                    <div className="diag-stat-card" style={{ padding: 8 }}>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>Sensitive (skipped)</div>
                      <div
                        style={{ fontSize: 20, fontWeight: 600, color: '#dc2626' }}
                        data-testid="ch-bug4-sensitive"
                      >
                        {chHealth.bug4_outcomes?.sensitive_skipped ?? 0}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hourly timeout buckets — bar list.
                    Only rendered when there ARE timeouts, else quiet. */}
                {chHealth.timeouts_per_hour && chHealth.timeouts_per_hour.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <h4 style={{
                      fontSize: 13, fontWeight: 600,
                      color: '#dc2626', marginBottom: 8,
                    }}>
                      Timeouts by hour (non-zero only)
                    </h4>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '160px 40px 1fr',
                        gap: 4,
                        fontSize: 11,
                        fontFamily: 'ui-monospace, monospace',
                      }}
                      data-testid="ch-timeouts-hourly"
                    >
                      {(() => {
                        const maxCount = Math.max(
                          1,
                          ...chHealth.timeouts_per_hour.map((r) => r.count),
                        );
                        return chHealth.timeouts_per_hour.map((r, i) => (
                          <React.Fragment key={i}>
                            <div>{r.hour_iso.replace('T', ' ').slice(0, 16)}</div>
                            <div style={{ textAlign: 'right' }}>{r.count}</div>
                            <div
                              style={{
                                background: '#fca5a5',
                                width: `${Math.max(4, (r.count / maxCount) * 100)}%`,
                                height: 12,
                                borderRadius: 3,
                              }}
                            />
                          </React.Fragment>
                        ));
                      })()}
                    </div>
                  </div>
                )}
                {(!chHealth.timeouts_per_hour ||
                  chHealth.timeouts_per_hour.length === 0) &&
                  chHealth.total_classifier_calls > 0 && (
                  <div
                    style={{
                      marginTop: 12, padding: 8,
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: 4,
                      color: '#059669', fontSize: 12,
                    }}
                    data-testid="ch-no-timeouts"
                  >
                    ✓ No classifier timeouts in the last 7 days.
                  </div>
                )}
              </>
            )}
            {!chHealth && !chHealthLoading && !chHealthErr && (
              <div
                style={{
                  padding: 12, color: '#6b7280', fontSize: 12,
                }}
              >
                Click Refresh to load classifier health data.
              </div>
            )}
          </section>

          <section className="diag-card" data-testid="diag-recent-card">
            <div className="diag-card-head">
              <h3>Recent Maintenance Requests (last 25)</h3>
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={fetchRecent}
                disabled={recentLoading}
                data-testid="diag-refresh-recent-btn"
              >
                {recentLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            <p className="diag-help">
              Rows sharing the same <code>tenant_id</code> within a short time window are highlighted — likely duplicate ingestions. Compare their <code>inbound_message_id</code> values directly.
            </p>
            {recentErr && <div className="admin-error" data-testid="diag-recent-error">{recentErr}</div>}
            {recentData && (() => {
              // Group rows by tenant_id + coarse time bucket (5 min) to
              // flag likely duplicate pairs visually. Purely a UI hint.
              const rows = recentData.rows || [];
              const groupKey = (r) => {
                if (!r.tenant_id || !r.created_at) return null;
                const t = Date.parse(r.created_at);
                if (isNaN(t)) return null;
                const bucket = Math.floor(t / (5 * 60 * 1000));
                return `${r.tenant_id}::${bucket}`;
              };
              const counts = {};
              rows.forEach(r => {
                const k = groupKey(r);
                if (k) counts[k] = (counts[k] || 0) + 1;
              });
              return (
                <div className="diag-recent-table-wrap" data-testid="diag-recent-table">
                  <table className="admin-table diag-recent-table">
                    <thead>
                      <tr>
                        <th>Created</th>
                        <th>Ref</th>
                        <th>Tenant</th>
                        <th>Source</th>
                        <th>inbound_message_id</th>
                        <th>subj_hash</th>
                        <th>body_hash</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => {
                        const k = groupKey(r);
                        const isDupCandidate = k && counts[k] > 1;
                        return (
                          <tr
                            key={r.id}
                            className={isDupCandidate ? 'diag-recent-dup' : ''}
                            data-testid={`diag-recent-row-${r.id}`}
                          >
                            <td className="diag-mono diag-nowrap">
                              {r.created_at ? r.created_at.replace('T', ' ').slice(0, 19) : '—'}
                            </td>
                            <td className="diag-mono">
                              <b>{r.contractor_ref || '—'}</b>
                              {isDupCandidate && <span className="diag-dup-badge">DUP?</span>}
                            </td>
                            <td className="diag-mono diag-truncate" title={r.tenant_id || ''}>
                              {r.tenant_id ? r.tenant_id.slice(-6) : '—'}
                            </td>
                            <td className="diag-mono">{r.source || '—'}</td>
                            <td className="diag-mono diag-msgid" title={r.inbound_message_id || ''}>
                              {r.inbound_message_id === null || r.inbound_message_id === undefined
                                ? <span className="diag-null">NULL</span>
                                : r.inbound_message_id}
                            </td>
                            <td className="diag-mono diag-truncate" title={r.inbound_subject_hash || ''}>
                              {r.inbound_subject_hash ? r.inbound_subject_hash.slice(0, 10) : '—'}
                            </td>
                            <td className="diag-mono diag-truncate" title={r.inbound_body_hash || ''}>
                              {r.inbound_body_hash ? r.inbound_body_hash.slice(0, 10) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </section>
        </div>
      )}

      {/* Harness tab — 2026-02 Message-803 Live Simulation. */}
      {activeTab === 'harness' && authenticated && (
        <HarnessPanel adminKey={adminKey} />
      )}
    </div>
  );
}

function ThresholdInput({ clientId, currentValue, onSave }) {
  const [value, setValue] = useState(currentValue ?? '');
  const [dirty, setDirty] = useState(false);

  const handleSave = () => {
    onSave(clientId, value === '' ? null : parseFloat(value));
    setDirty(false);
  };

  return (
    <div className="threshold-input-wrap">
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={e => { setValue(e.target.value); setDirty(true); }}
        className="threshold-input"
        placeholder="Set..."
        data-testid={`threshold-input-${clientId}`}
      />
      {dirty && (
        <button className="threshold-save" onClick={handleSave} data-testid={`threshold-save-${clientId}`}>
          Save
        </button>
      )}
    </div>
  );
}
